import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { PackagePlus, Check } from 'lucide-react';
import '../nativeinventory/NativeInventoryAdjustmentNew.css';
import './NativeItems.css';

// New Item (Native) — the record type with no NetSuite workaround. Template-
// based: pick a similar existing item and the new one copies its accounts,
// tax schedule, units, class, and subsidiaries (the NS-required refs no form
// should ask a human for). Native row first; NetSuite create best-effort.

export default function NativeItemNew() {
  const [searchParams] = useSearchParams();
  const [template, setTemplate] = useState(null);
  const [tplQuery, setTplQuery] = useState('');
  const [tplResults, setTplResults] = useState([]);
  const [form, setForm] = useState({ sku: '', name: '', itemType: '', description: '', purchaseDescription: '', weight: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // ?copyFrom=<sku> — "Make Copy" for items: template + fields prefill.
  useEffect(() => {
    const copyFrom = searchParams.get('copyFrom');
    if (!copyFrom) return;
    axios.get(`/api/native-items/${encodeURIComponent(copyFrom)}`)
      .then(({ data }) => {
        if (!data.item) return;
        setTemplate({ id: data.item.id, sku: data.item.sku, name: data.item.name, itemType: data.item.itemType });
        setForm((f) => ({
          ...f,
          name: data.item.name || '',
          itemType: data.item.itemType || '',
          description: data.item.description || '',
          purchaseDescription: data.item.purchaseDescription || '',
          weight: data.item.weight != null ? String(data.item.weight) : '',
        }));
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (tplQuery.trim().length < 2 || template) { setTplResults([]); return undefined; }
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get('/api/native-items', { params: { q: tplQuery.trim(), pageSize: 12 } });
        setTplResults(data.items || []);
      } catch {
        setTplResults([]);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [tplQuery, template]);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const { data } = await axios.post('/api/native-items', {
        sku: form.sku.trim(),
        name: form.name.trim(),
        templateSku: template.sku,
        itemType: form.itemType || undefined,
        description: form.description.trim() || undefined,
        purchaseDescription: form.purchaseDescription.trim() || undefined,
        weight: form.weight === '' ? undefined : Number(form.weight),
      });
      setConfirmation(data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmation) {
    return (
      <div className="nia-page">
        <div className="nia-card nia-confirm">
          <div className="nia-confirm-icon"><Check size={28} /></div>
          <h2><code>{confirmation.item.sku}</code> created</h2>
          <div className="nia-confirm-grid">
            <span>NetSuite</span>
            <strong>{confirmation.nsSync === 'synced' ? `created (${confirmation.item.netsuiteId})` : `pending — ${confirmation.nsError || 'retry when NS is reachable'}`}</strong>
          </div>
          <p className="nia-note">{confirmation.note}</p>
          <div className="nia-actions">
            <Link className="nia-btn nia-btn-primary" to={`/native-items/${encodeURIComponent(confirmation.item.sku)}`} style={{ textDecoration: 'none' }}>Open item</Link>
            <button className="nia-btn nia-btn-ghost" onClick={() => window.location.reload()}>New item</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="nia-page">
      <div className="nia-header">
        <h1><PackagePlus size={20} /> New Item (Native)</h1>
      </div>

      <div className="nia-card">
        <h3>1. Template — a similar existing item</h3>
        <p className="nia-hint">The new item copies the template's accounts, tax schedule, units, class, and subsidiaries — pick the closest match (same brand + kind).</p>
        {!template ? (
          <>
            <input
              className="nia-input nia-input-search"
              placeholder="Search items by SKU or name…"
              value={tplQuery}
              onChange={(e) => setTplQuery(e.target.value)}
            />
            {tplResults.length > 0 && (
              <div className="nia-results">
                {tplResults.map((r) => (
                  <button key={r.id} className="nia-result" onClick={() => {
                    setTemplate({ id: r.id, sku: r.sku, name: r.name, itemType: r.itemType || r.item_type });
                    setForm((f) => ({ ...f, itemType: r.itemType || r.item_type || '' }));
                    setTplQuery('');
                    setTplResults([]);
                  }}>
                    <code>{r.sku}</code> {r.name}
                    <span className="nia-result-muted">{r.itemType || r.item_type}</span>
                  </button>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="nia-chip">
            <code>{template.sku}</code> {template.name}
            <span className="nia-sub">{template.itemType}</span>
            <button className="nia-icon-btn" onClick={() => setTemplate(null)}>×</button>
          </div>
        )}
      </div>

      <div className="nia-card">
        <h3>2. The new item</h3>
        <div className="nit-form" style={{ maxWidth: 560 }}>
          <label><span>SKU</span><input value={form.sku} onChange={set('sku')} placeholder="e.g. RW21430PLM" maxLength={60} /></label>
          <label><span>Name</span><input value={form.name} onChange={set('name')} maxLength={200} /></label>
          <label>
            <span>Type</span>
            <select className="nia-input nia-select" value={form.itemType} onChange={set('itemType')}>
              <option value="">Same as template ({template?.itemType || '—'})</option>
              <option value="InvtPart">Inventory item (purchased)</option>
              <option value="Assembly">Assembly (built)</option>
            </select>
          </label>
          <label><span>Sales description</span><textarea rows={2} value={form.description} onChange={set('description')} maxLength={999} /></label>
          <label><span>Purchase description</span><textarea rows={2} value={form.purchaseDescription} onChange={set('purchaseDescription')} maxLength={999} /></label>
          <label><span>Weight (optional)</span><input inputMode="decimal" value={form.weight} onChange={set('weight')} style={{ maxWidth: 120 }} /></label>
        </div>
        {error && <div className="nia-error" style={{ marginTop: 10 }}>{error}</div>}
        <div className="nia-actions" style={{ marginTop: 12 }}>
          <button
            className="nia-btn nia-btn-primary"
            onClick={submit}
            disabled={submitting || !template || !form.sku.trim() || !form.name.trim()}
          >
            {submitting ? 'Creating…' : 'Create item'}
          </button>
        </div>
        <p className="nia-hint" style={{ marginTop: 8 }}>
          Creates natively first, then in NetSuite. Assemblies start with an empty BOM — add components on the item's BOM tab after creating.
        </p>
      </div>
    </div>
  );
}
